import { Project, Site, User, Document, ProjectListWithGenerationSummary } from "./types";
import { getCookie } from "./utils";

const getProjectsWithGenerationSummary = async (): Promise<ProjectListWithGenerationSummary> => {
    const response = await fetch('/api/projects/summary/');
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch project summary');
    }
    const data = await response.json();
    data.instances.forEach((project: Project) => {
        if (project.sites) {
            project.sites.forEach((site: Site) => {
                site.latitude = Number(site.latitude);
                site.longitude = Number(site.longitude);
            });
        }
    });
    return data as ProjectListWithGenerationSummary;
};

const getCurrentUser = async (): Promise<User> => {
    const response = await fetch('/api/users/current/');
    if (response.status === 401) {
        throw new Error('User not authenticated');
    }
    if (!response.ok) {
        throw new Error('Failed to fetch user data');
    }
    return response.json() as Promise<User>;
};

const login = async (username: string, password: string): Promise<void> => {
    const response = await fetch('/api/users/login/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
    });
    if (!response.ok) {
        throw new Error('Login failed');
    }
};

const logout = async (): Promise<void> => {
    const response = await fetch('/api/users/logout/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken') || '',
        }
    });
    if (!response.ok) {
        throw new Error('Logout failed');
    }
};

const createProject = async (projectData: { name: string; country: string; locality?: string; budget?: number }): Promise<Project> => {
    const response = await fetch('/api/projects/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: JSON.stringify(projectData)
    });
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create project');
    }
    const data = await response.json();
    // Convert coordinates to numbers if sites exist
    if (data.sites) {
        data.sites.forEach((site: Site) => {
            site.latitude = Number(site.latitude);
            site.longitude = Number(site.longitude);
        });
    }
    return data as Project;
};

const uploadDocument = async (projectId: number, file: File, name: string): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('project', projectId.toString());

    const response = await fetch('/api/documents/', {
        method: 'POST',
        headers: {
            'X-CSRFToken': getCookie('csrftoken') || '',
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload document');
    }

    return response.json() as Promise<Document>;
};

const deleteDocument = async (documentId: number): Promise<void> => {
    const response = await fetch(`/api/documents/${documentId}/`, {
        method: 'DELETE',
        headers: {
            'X-CSRFToken': getCookie('csrftoken') || '',
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete document');
    }
};

const getProjectDocuments = async (projectId: number): Promise<Document[]> => {
    const response = await fetch(`/api/documents/project/${projectId}/`);
    
    if (!response.ok) {
        throw new Error('Failed to fetch documents');
    }
    
    return response.json() as Promise<Document[]>;
};

export { getProjectsWithGenerationSummary, getCurrentUser, login, logout, createProject, uploadDocument, deleteDocument, getProjectDocuments };